import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { MDBContainer, MDBBtn, MDBInput } from "mdb-react-ui-kit";
import { usePageState } from "../context/AppStateContext";
import "./ViewProductsPage.css";

// Dummy products
const products = [
  {
    id: 1,
    title: "Nike Air Max",
    description: "Comfortable running shoes",
    price: 120,
    category: "Shoes",
    condition: "New",
    quantity: 10,
    sku: "NK-001",
    brand: "Nike",
    uploadedImages: ["https://via.placeholder.com/120"],
    selectedMarketplaces: ["ebay", "shopify"],
    marketplaces: {
      ebay: {
        Brand: "Nike",
        Size: "10",
        Color: "Black",
        SKU: "EB-NK-001",
        Price: "$120",
        Inventory: 20,
      },
      shopify: {
        SKU: "NK-001",
        Inventory: 15,
        MetaField: "Seasonal",
        Price: "$120",
      },
    },
  },
  {
    id: 2,
    title: "Adidas Hoodie",
    description: "Warm and cozy hoodie",
    price: 75,
    category: "Clothing",
    condition: "New",
    quantity: 25,
    sku: "AD-202",
    brand: "Adidas",
    uploadedImages: ["https://via.placeholder.com/120/FF0000"],
    selectedMarketplaces: ["poshmark"],
    marketplaces: {
      poshmark: {
        Brand: "Adidas",
        Size: "M",
        Color: "Blue",
        Price: "$75",
        Inventory: 12,
      },
    },
  },
  {
    id: 3,
    title: "Apple Watch",
    description: "Series 6 smartwatch",
    price: 299,
    category: "Electronics",
    condition: "New",
    quantity: 8,
    sku: "AW-006",
    brand: "Apple",
    uploadedImages: ["https://via.placeholder.com/120/00FF00"],
    selectedMarketplaces: ["ebay", "shopify", "poshmark"],
    marketplaces: {
      ebay: {
        Model: "Series 6",
        Color: "Black",
        SKU: "EB-AW-006",
        Price: "$299",
      },
      shopify: { SKU: "AW-006", Inventory: 8, Price: "$299" },
      poshmark: { Model: "Series 6", Price: "$299", Inventory: 5 },
    },
  },
  {
    id: 4,
    title: "Samsung Galaxy Buds",
    description: "Wireless earbuds with ANC",
    price: 149,
    category: "Electronics",
    condition: "New",
    quantity: 15,
    sku: "SG-004",
    brand: "Samsung",
    uploadedImages: ["https://via.placeholder.com/120/0000FF"],
    selectedMarketplaces: ["ebay", "shopify"],
    marketplaces: {
      ebay: { Model: "Galaxy Buds Pro", Color: "White", Price: "$149" },
      shopify: { SKU: "SG-004", Inventory: 15, Price: "$149" },
    },
  },
  {
    id: 5,
    title: "Puma Running Shorts",
    description: "Lightweight athletic shorts",
    price: 35,
    category: "Clothing",
    condition: "New",
    quantity: 50,
    sku: "PM-005",
    brand: "Puma",
    uploadedImages: ["https://via.placeholder.com/120/FFFF00"],
    selectedMarketplaces: ["poshmark"],
    marketplaces: {
      poshmark: { Brand: "Puma", Size: "L", Color: "Black", Price: "$35" },
    },
  },
  {
    id: 6,
    title: "New Balance 574",
    description: "Classic lifestyle sneakers",
    price: 89,
    category: "Shoes",
    condition: "New",
    quantity: 20,
    sku: "NB-006",
    brand: "New Balance",
    uploadedImages: ["https://via.placeholder.com/120/FF00FF"],
    selectedMarketplaces: ["ebay", "shopify"],
    marketplaces: {
      ebay: { Brand: "New Balance", Size: "9", Color: "Grey", Price: "$89" },
      shopify: { SKU: "NB-006", Inventory: 20, Price: "$89" },
    },
  },
];

const marketplaces = [
  { id: "ebay", name: "eBay", color: "primary" },
  { id: "shopify", name: "Shopify", color: "success" },
  { id: "poshmark", name: "Poshmark", color: "secondary" },
];

// Get unique categories from products
const categories = [...new Set(products.map((p) => p.category))];

const ViewProductsPage = () => {
  const navigate = useNavigate();

  // =============================================
  // USE GLOBAL STATE (replaces local useState)
  // State persists across route changes & page refresh
  // =============================================
  const { state, setState, clearState } = usePageState("viewProducts");

  // Destructure state with defaults
  const {
    currentPage = 1,
    itemsPerPage = 5,
    searchQuery = "",
    categoryFilter = "",
    marketplaceFilter = "",
    selectedProductIds = [],
    expandedProduct = null,
  } = state;

  // =============================================
  // FILTERED PRODUCTS
  // =============================================
  const filteredProducts = useMemo(() => {
    return products.filter((product) => {
      // Search filter
      const matchesSearch =
        searchQuery === "" ||
        product.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.description.toLowerCase().includes(searchQuery.toLowerCase());

      // Category filter
      const matchesCategory =
        categoryFilter === "" || product.category === categoryFilter;

      // Marketplace filter
      const matchesMarketplace =
        marketplaceFilter === "" ||
        product.selectedMarketplaces.includes(marketplaceFilter);

      return matchesSearch && matchesCategory && matchesMarketplace;
    });
  }, [searchQuery, categoryFilter, marketplaceFilter]);

  // =============================================
  // PAGINATION
  // =============================================
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedProducts = filteredProducts.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  // Handle page change
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setState({ currentPage: page });
    }
  };

  // =============================================
  // SELECTION HANDLERS
  // =============================================
  const handleSelectProduct = (productId) => {
    const isSelected = selectedProductIds.includes(productId);
    const newSelection = isSelected
      ? selectedProductIds.filter((id) => id !== productId)
      : [...selectedProductIds, productId];
    setState({ selectedProductIds: newSelection });
  };

  const handleSelectAll = () => {
    const allIds = paginatedProducts.map((p) => p.id);
    const allSelected = allIds.every((id) => selectedProductIds.includes(id));
    if (allSelected) {
      // Deselect all on current page
      setState({
        selectedProductIds: selectedProductIds.filter(
          (id) => !allIds.includes(id)
        ),
      });
    } else {
      // Select all on current page
      setState({
        selectedProductIds: [...new Set([...selectedProductIds, ...allIds])],
      });
    }
  };

  // Toggle expanded product details
  const handleToggleExpand = (productId) => {
    setState({
      expandedProduct: expandedProduct === productId ? null : productId,
    });
  };

  // =============================================
  // CLEAR ALL FILTERS (resets to defaults)
  // =============================================
  const handleClearFilters = () => {
    clearState();
  };

  return (
    <MDBContainer className="py-4">
      <h2 className="mb-4">View Products</h2>

      {/* ============================================ */}
      {/* FILTERS SECTION */}
      {/* ============================================ */}
      <div className="filters-section p-3 mb-4 bg-light rounded shadow-sm">
        <div className="row g-3 align-items-end">
          {/* Search */}
          <div className="col-md-3">
            <MDBInput
              label="Search products..."
              value={searchQuery}
              onChange={(e) =>
                setState({ searchQuery: e.target.value, currentPage: 1 })
              }
            />
          </div>

          {/* Category Filter */}
          <div className="col-md-2">
            <select
              className="form-select"
              value={categoryFilter}
              onChange={(e) =>
                setState({ categoryFilter: e.target.value, currentPage: 1 })
              }
            >
              <option value="">All Categories</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>

          {/* Marketplace Filter */}
          <div className="col-md-2">
            <select
              className="form-select"
              value={marketplaceFilter}
              onChange={(e) =>
                setState({ marketplaceFilter: e.target.value, currentPage: 1 })
              }
            >
              <option value="">All Marketplaces</option>
              {marketplaces.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </div>

          {/* Items per page */}
          <div className="col-md-2">
            <select
              className="form-select"
              value={itemsPerPage}
              onChange={(e) =>
                setState({
                  itemsPerPage: parseInt(e.target.value),
                  currentPage: 1,
                })
              }
            >
              <option value={5}>5 per page</option>
              <option value={10}>10 per page</option>
              <option value={20}>20 per page</option>
            </select>
          </div>

          {/* Clear Filters */}
          <div className="col-md-3">
            <MDBBtn color="secondary" size="sm" onClick={handleClearFilters}>
              <i className="fas fa-times me-1"></i> Clear Filters
            </MDBBtn>
          </div>
        </div>

        {/* Active Filters Summary */}
        {(searchQuery || categoryFilter || marketplaceFilter) && (
          <div className="mt-2">
            <small className="text-muted">
              Showing {filteredProducts.length} of {products.length} products
              {searchQuery && <span className="badge bg-info ms-2">Search: "{searchQuery}"</span>}
              {categoryFilter && <span className="badge bg-info ms-2">Category: {categoryFilter}</span>}
              {marketplaceFilter && <span className="badge bg-info ms-2">Marketplace: {marketplaceFilter}</span>}
            </small>
          </div>
        )}
      </div>

      {/* ============================================ */}
      {/* SELECTION INFO */}
      {/* ============================================ */}
      {selectedProductIds.length > 0 && (
        <div className="alert alert-primary mb-3">
          <strong>{selectedProductIds.length}</strong> product(s) selected
          <MDBBtn
            color="link"
            size="sm"
            className="ms-2"
            onClick={() => setState({ selectedProductIds: [] })}
          >
            Clear Selection
          </MDBBtn>
        </div>
      )}

      {/* ============================================ */}
      {/* SELECT ALL CHECKBOX */}
      {/* ============================================ */}
      <div className="mb-3">
        <div className="form-check">
          <input
            className="form-check-input"
            type="checkbox"
            id="selectAll"
            checked={
              paginatedProducts.length > 0 &&
              paginatedProducts.every((p) => selectedProductIds.includes(p.id))
            }
            onChange={handleSelectAll}
          />
          <label className="form-check-label" htmlFor="selectAll">
            Select All on Page
          </label>
        </div>
      </div>

      {/* ============================================ */}
      {/* PRODUCT LIST */}
      {/* ============================================ */}
      <div className="product-list">
        {paginatedProducts.length === 0 ? (
          <div className="text-center py-5 text-muted">
            <i className="fas fa-box-open fa-3x mb-3"></i>
            <p>No products found matching your filters.</p>
          </div>
        ) : (
          paginatedProducts.map((product) => (
            <div
              key={product.id}
              className={`product-row p-3 mb-3 shadow-sm rounded ${selectedProductIds.includes(product.id) ? "border-primary border-2" : ""
                }`}
              style={{ backgroundColor: "#fff" }}
            >
              <div className="d-flex align-items-center justify-content-between">
                {/* Selection Checkbox + Product Info */}
                <div className="d-flex align-items-center">
                  <div className="form-check me-3">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={selectedProductIds.includes(product.id)}
                      onChange={() => handleSelectProduct(product.id)}
                    />
                  </div>
                  <img
                    src={product.uploadedImages[0]}
                    alt={product.title}
                    className="img-thumbnail me-3"
                    style={{
                      width: "120px",
                      height: "120px",
                      objectFit: "cover",
                    }}
                  />
                  <div>
                    <h5 className="mb-1">{product.title}</h5>
                    <p className="mb-1 text-muted">{product.description}</p>
                    <div className="d-flex flex-wrap gap-2 mb-2">
                      <span className="badge bg-secondary">
                        Price: ${product.price}
                      </span>
                      <span className="badge bg-secondary">
                        Category: {product.category}
                      </span>
                      <span className="badge bg-secondary">
                        Condition: {product.condition}
                      </span>
                      <span className="badge bg-secondary">
                        Quantity: {product.quantity}
                      </span>
                    </div>
                    <div className="mb-2">
                      {product.selectedMarketplaces.map((id) => {
                        const market = marketplaces.find((m) => m.id === id);
                        return (
                          <span key={id} className="market-badge">
                            {market?.name}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="d-flex flex-column gap-2 ms-3">
                  <MDBBtn
                    size="sm"
                    color="info"
                    outline
                    onClick={() => handleToggleExpand(product.id)}
                  >
                    {expandedProduct === product.id ? "Collapse" : "Expand"}
                  </MDBBtn>
                  <MDBBtn
                    size="sm"
                    color="danger"
                    onClick={() =>
                      navigate(`/products/${product.id}`, {
                        state: { product },
                      })
                    }
                  >
                    View Details
                  </MDBBtn>
                  <MDBBtn
                    size="sm"
                    color="warning"
                    onClick={() => navigate(`/products/${product.id}?edit=true`)}
                  >
                    Update
                  </MDBBtn>
                  <MDBBtn size="sm" color="danger">
                    Delete
                  </MDBBtn>
                </div>
              </div>

              {/* Expanded Marketplace Details */}
              {expandedProduct === product.id && (
                <div className="mt-3 border-top pt-3">
                  {product.selectedMarketplaces.map((id) => {
                    const market = marketplaces.find((m) => m.id === id);
                    const fields = product.marketplaces[id] || {};
                    return (
                      <div
                        key={id}
                        className={`marketplace-card p-3 mb-2 rounded border border-${market?.color}`}
                      >
                        <h6 className={`text-${market?.color} mb-3`}>
                          {market?.name} Details
                        </h6>
                        <div className="row">
                          {Object.entries(fields).map(([k, v]) => (
                            <div key={k} className="col-md-4 mb-2">
                              <small className="text-muted">{k}:</small>{" "}
                              <span>{v}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* ============================================ */}
      {/* PAGINATION */}
      {/* ============================================ */}
      {totalPages > 1 && (
        <nav className="mt-4">
          <ul className="pagination justify-content-center">
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <button
                className="page-link"
                onClick={() => handlePageChange(currentPage - 1)}
              >
                Previous
              </button>
            </li>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <li
                key={page}
                className={`page-item ${currentPage === page ? "active" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageChange(page)}
                >
                  {page}
                </button>
              </li>
            ))}
            <li
              className={`page-item ${currentPage === totalPages ? "disabled" : ""
                }`}
            >
              <button
                className="page-link"
                onClick={() => handlePageChange(currentPage + 1)}
              >
                Next
              </button>
            </li>
          </ul>
          <p className="text-center text-muted">
            Page {currentPage} of {totalPages} ({filteredProducts.length} items)
          </p>
        </nav>
      )}
    </MDBContainer>
  );
};

export default ViewProductsPage;
